/**
 *  Copyright (c) 2022, 2023 Texas Instruments Incorporated
 *  All rights reserved.
 *
 *  Redistribution and use in source and binary forms, with or without
 *  modification, are permitted provided that the following conditions
 *  are met:
 *
 *  *   Redistributions of source code must retain the above copyright
 *  notice, this list of conditions and the following disclaimer.
 *  notice, this list of conditions and the following disclaimer in the
 *  documentation and/or other materials provided with the distribution.
 *  *   Neither the name of Texas Instruments Incorporated nor the names of
 *  its contributors may be used to endorse or promote products derived
 *  from this software without specific prior written permission.
 *
 *  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 *  AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO,
 *  THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR
 *  PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR
 *  CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 *  EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO,
 *  PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS;
 *  OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 *  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR
 *  OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE,
 *  EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
import { Component, h } from '@stencil/core';

@Component({
    tag: 'app-getting-started',
    styleUrl: 'app-getting-started.scss',
    shadow: true,
})
export class AppGettingStarted {
    render() {
        return (
            <section>
                <gc-widget-container heading={'Getting started guide'}>

                    <p>
                        When starting development, there are many items required before work can begin. You need a development board like a&nbsp;
                        <a href='https://www.ti.com/ww/en/launchpad/launchpad.html?DCMP=ep-mcu-msp-432-en&amps;HQS=ep-mcu-msp-432-toolsinsider-20150331-lp-lpad-en'>LaunchPad</a>&nbsp;or&nbsp;
                        <a href='https://www.ti.com/ww/en/wireless_connectivity/sensortag2015/?INTC=SensorTag&amps;HQS=sensortag'>SensorTag</a>,
                        some sort of development environment, driver libraries, examples and the documentation that explains how to use the device, tools and software. Finding
                        and setting up all of these items can take significant time. When your new kit arrives, the last thing you want to do is find and install all of these
                        other items. You want to start using it as soon as you open the box. This is where cloud-based tools shine.
                    </p>


                    <p class='group'>
                        <h2>Find what you need with Resource Explorer</h2>
                        <p>
                            For most users we recommend starting with Resource Explorer.  Resource Explorer helps you find all the latest examples, libraries, demo applications and
                            documentation for your chosen platform.  Select the device or board of your choice and it shows you everything that is available.  Since Resource Explorer
                            is fully integrated with TI’s cloud tools, your files are imported directly into your project to get you developing with just a few clicks. A big
                            advantage of working in the cloud is that you don't have to worry about not having the latest documentation or examples as it is always the latest version
                        </p>

                        <p>
                            Use the drop down menu at the top of Resource Explorer to filter all of the content to match the device or development kit that you are using.  Alternatively
                            you can browse through everything available or use the search box to locate a specific example or document.
                        </p>
                    </p>


                    <p class='group'>
                        <h2>Try a demo application</h2>
                        <p>
                            One great place to start is a demo application.  Demo applications are pre-built examples that you can load onto your LaunchPad or SensorTag directly from
                            Resource Explorer. They are located in the examples section and are noted with special icon. These provide a graphical user interface in your browser that
                            communicates with the application running on the development boards to control and demonstrate some of the functionality of the selected device. The first
                            time you run a demo application you will be prompted to install a small software agent that enables the user interface for the demo to communicate with
                            your development board.
                        </p>
                    </p>

                    <p class='group'>
                        <h2>Import into cloud</h2>
                        <p>
                            When you are ready to begin creating your own applications you have a few options.  You can start by selecting one of the examples in Resource Explorer and
                            then click on the import to CCS cloud button.  This will start the Code Composer Studio Cloud development environment in another tab in your browser and import
                            the selected project.  You can then edit the source code, build the project and program it onto your development board.
                        </p>
                    </p>

                    <p class='group'>
                        <h2>Energia</h2>
                        <p>
                            Another option is to start with Energia.  Energia is an open-source electronics prototyping platform that brings the Wiring and Arduino framework to the Texas
                            Instruments development kits. Energia offers a robust framework of easy-to-use APIs for enabling common functionality across various target microcontroller
                            platforms. You can browse through the Energia examples in Resource Explorer or start Code Composer Studio Cloud and import them from there.
                        </p>
                    </p>

                    <p class='group'>
                        <h2>Getting started with GUI Composer</h2>
                        <p>These tutorials walk you through all the steps required to create a GUI front end that interacts with your target device:</p>
                        <ul>
                            <li>
                                If you have a CC3200, check out the <a href='/gc/v2/help/GC_UserGuide_v2/IoT.html'>IoT Dashboard Tutorial:</a> It shows how to display live sensor
                                data from your target device
                            </li>
                            <li>
                                If you have an MSP430F5529 device, check out the <a href='/gc/v2/help/GC_UserGuide_v2/serial.html'>Serial I/O Tutorial:</a> It takes the classic 'Blinky'
                                example (the 'hello world' for MCUs) and adds a cool Web display that shows the status of the LED on your target device.
                            </li>
                        </ul>
                    </p>
                </gc-widget-container>
            </section>
        );
    }
}
